import React, { ReactElement, useState } from "react";
import { FormattedMessage } from "react-intl";

import { isNil } from "ramda";

import IconButton from "@mui/material/IconButton";
import MenuItem from "@mui/material/MenuItem";
import Paper from "@mui/material/Paper";
import Select from "@mui/material/Select";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";

import { ipRegex } from "constants/regexp";

import IconSelector from "components/shared/images/IconSelector";

import { IFirewallRuleData } from "types/api/baremetalApiInterface";

const EditingRule = ({
 rule,
 confirmEdit,
 confirmDelete
}: {
 rule: IFirewallRuleData;
 confirmEdit: (newRule: IFirewallRuleData) => void;
 confirmDelete: () => void;
}): ReactElement => {
 const [ruleData, setRuleData] = useState<IFirewallRuleData>(rule);

 const handleChangeData = (field: keyof IFirewallRuleData, data: string | number) => {
  setRuleData({ ...ruleData, [field]: data });
 };

 return (
  <Paper sx={{ p: 2, minWidth: 950 }}>
   <Stack direction="row" spacing={2} textAlign="left" justifyContent="space-between">
    <Stack>
     <Typography fontWeight="bold" variant="caption">
      <FormattedMessage id="baremetal.firewall.name" />
     </Typography>
     <TextField
      value={ruleData.name}
      onChange={(event) => handleChangeData("name", event.target.value)}
      fullWidth={true}
      error={ruleData.name === ""}
     />
    </Stack>
    <Stack>
     <Typography fontWeight="bold" variant="caption">
      <FormattedMessage id="baremetal.firewall.protocol" />
     </Typography>
     <Select
      value={ruleData.protocol}
      onChange={(event) => handleChangeData("protocol", event.target.value as string)}>
      <MenuItem key="tcp" value="tcp">
       TCP
      </MenuItem>
      <MenuItem key="udp" value="udp">
       UDP
      </MenuItem>
      <MenuItem key="gre" value="gre">
       GRE
      </MenuItem>
      <MenuItem key="icmp" value="icmp">
       ICMP
      </MenuItem>
      <MenuItem key="ipip" value="ipip">
       IPIP
      </MenuItem>
      <MenuItem key="ah" value="ah">
       AH
      </MenuItem>
      <MenuItem key="esp" value="esp">
       ESP
      </MenuItem>
     </Select>
    </Stack>
    <Stack>
     <Typography fontWeight="bold" variant="caption">
      <FormattedMessage id="baremetal.firewall.sourceIp" />
     </Typography>
     <TextField
      value={ruleData.src_ip}
      onChange={(event) => handleChangeData("src_ip", event.target.value)}
      fullWidth={true}
      error={ruleData.src_ip === "" || !ipRegex.test(ruleData.src_ip || "")}
     />
    </Stack>
    <Stack>
     <Typography fontWeight="bold" variant="caption">
      <FormattedMessage id="baremetal.firewall.destinationIp" />
     </Typography>
     <TextField
      value={ruleData.dst_ip}
      onChange={(event) => handleChangeData("dst_ip", event.target.value)}
      fullWidth={true}
      error={ruleData.dst_ip === "" || !ipRegex.test(ruleData.dst_ip || "")}
     />
    </Stack>
    <Stack>
     <Typography fontWeight="bold" variant="caption">
      <FormattedMessage id="baremetal.firewall.sourcePort" />
     </Typography>
     <TextField
      value={Number(ruleData.src_port)}
      onChange={(event) => handleChangeData("src_port", event.target.value)}
      fullWidth={true}
      type="number"
      error={
       !isNil(ruleData.src_port) &&
       (Number(ruleData.src_port) < 1 || Number(ruleData?.src_port) > 65535)
      }
     />
    </Stack>
    <Stack>
     <Typography fontWeight="bold" variant="caption">
      <FormattedMessage id="baremetal.firewall.destinationPort" />
     </Typography>
     <TextField
      value={Number(ruleData.dst_port)}
      onChange={(event) => handleChangeData("dst_port", event.target.value)}
      fullWidth={true}
      type="number"
      error={
       !isNil(ruleData.dst_port) &&
       (Number(ruleData.dst_port) < 1 || Number(ruleData?.dst_port) > 65535)
      }
     />
    </Stack>
    <Stack>
     <Typography fontWeight="bold" variant="caption">
      <FormattedMessage id="baremetal.firewall.action" />
     </Typography>
     <Select
      value={ruleData.action}
      onChange={(event) => handleChangeData("action", event.target.value as string)}>
      <MenuItem key="accept" value="accept">
       ACCEPT
      </MenuItem>
      <MenuItem key="discard" value="discard">
       DISCARD
      </MenuItem>
     </Select>
    </Stack>
    <Stack direction="row" spacing={1} alignItems="center" justifyContent="center">
     <IconButton onClick={() => confirmEdit(ruleData)}>
      <IconSelector icon="CheckCircleIcon" props={{ color: "kxOrange" }} />
     </IconButton>
     <IconButton onClick={confirmDelete}>
      <IconSelector icon="DeleteIcon" props={{ color: "kxOrange", sx: { fontSize: 18 } }} />
     </IconButton>
    </Stack>
   </Stack>
  </Paper>
 );
};

export default EditingRule;
